import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { GetStaticProps } from "next"

import { useLang } from "hooks/useLang"
import { EventJsonLd, NextSeo, BreadcrumbJsonLd } from "next-seo"
import React, { useEffect, useRef, useState } from "react"
import { IFinalEvent } from "types/event"
import useWindowSize from "hooks/useWindowSize"
import removeMarkdown from "utils/helpers/removeMarkdown"
import { useAnalytics } from "components/Analytics/UnifiedAnalyticsProvider"
import { useAnalyticsTracking } from "hooks/useAnalyticsTracking"
import { ITicket } from "types/ticket"
import EventPageSkeleton from "components/Skeleton/EventPageSkeleton"
import { useQuery } from "@tanstack/react-query"
import { fetchEvent, fetchEventOptimized } from "api"

import { useCartDispatch } from "hooks/useCartV2"
import { ImprovedCartActionsEnum } from "types/improved-booking"
import F1Layout from "components/Layout/F1Layout"
import GridEventContent from "components/EventDetailsPage/grid/GridEventContent"
import { withMaintenanceCheck } from "utils/withMaintenanceCheck"
import { MaintenanceAwareProps } from "types/maintenance"

const EventHero = dynamic(
    () => import("components/EventDetailsPage/EventHero"),
    { ssr: false }
)

export type FetchEventResponse = {
    message: string
    payload: {
        event: IFinalEvent
        tickets: ITicket[]
    }
}

// export const getStaticProps: GetStaticProps<Props> =
//     withMaintenanceCheck<Props>(async () => {
//         const data = await fetchEvent(process.env.NEXT_PUBLIC_CURRENT_F1_ID)
//         // if (!data.payload) {
//         //     return {
//         //         notFound: true,
//         //         props: {} as Props, // Add empty props to satisfy type system
//         //     }
//         // }
//         return {
//             props: {
//                 event: data.payload.event,
//                 tickets: data.payload.tickets,
//             },
//             revalidate: 180, // 10 minutes
//         }
//     })

async function fetchEventDetails(
    eventId: string
): Promise<{ event: IFinalEvent; tickets: ITicket[] }> {
    const data = await fetchEvent(eventId)
    if (!data.payload) {
        throw new Error("Failed to fetch event data")
    }
    return {
        // @ts-ignore
        event: data.payload,
        // @ts-ignore
        tickets: data.payload.tickets,
    }
}

const F1EventPage = () => {
    const { isTabletOrSmaller, isDesktop } = useWindowSize()

    const { isAr } = useLang()

    const router = useRouter()

    const [openVerificationModal, setOpenVerificationModal] =
        useState<boolean>(false)
    const [showActionCard, setShowActionCard] = useState(false)
    const [isHeaderFixed, setIsHeaderFixed] = useState(false)

    const cartDispatch = useCartDispatch()
    /*
     ** The event page is statically generated every 3 minutes.
     ** The event HTML will be cached for 10 minutes.
     ** Event data (timeslots, tickets, etc.) will be cached for 1 minute and automatically refetched and updated every 3 minutes without a page refresh.
     ** Event data is considered stale after 1 minute; any user action on a component consuming the cached data after this will trigger a refetch (ex: clicking action card).
     */

    const currentF1Id = process.env.NEXT_PUBLIC_CURRENT_F1_ID

    const { data, error, isLoading } = useQuery({
        queryKey: ["event", currentF1Id],
        queryFn: () => fetchEventDetails(currentF1Id),
        // initialData: { event, tickets },
        // refetchInterval: 180000, // 3 min
        // staleTime: 60000, // 1 min
        // refetchOnWindowFocus: true,
    })

    useEffect(() => {
        const handleScroll = () => {
            if (!isDesktop) {
                setShowActionCard(true)
                setIsHeaderFixed(false)
                return
            }
            const scrollPosition = window.scrollY
            const showThreshold = 200 // Show card after scrolling 200px
            setShowActionCard(scrollPosition > showThreshold)
            setIsHeaderFixed(scrollPosition <= 50)
        }

        handleScroll()

        window.addEventListener("scroll", handleScroll)
        return () => window.removeEventListener("scroll", handleScroll)
    }, [isDesktop])

    useEffect(() => {
        return () => {
            cartDispatch({ type: ImprovedCartActionsEnum.CLEAR_CART })
        }
    }, [cartDispatch])

    // const { data: videoData } = useQuery({
    //     queryKey: ["eventVideo", currentF1Id],
    //     queryFn: () => fetchVideoInfo(),
    //     refetchOnWindowFocus: false,
    //     staleTime: 300000, // 5 min
    // })

    const updatedEvent = data?.event!

    const getDesktopPoster = (ev: IFinalEvent) => {
        const posterPath = ev?.media?.posters?.web_poster?.original || ""
        const defaultImageUrl = "/svg/v2/pass_ticket_fallback_image_light.png"
        return posterPath
            ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterPath}`
            : defaultImageUrl
    }
    const getMobilePoster = (ev: IFinalEvent) => {
        const posterPath = ev?.media?.posters?.mobile_poster?.original || ""
        const defaultImageUrl = "/svg/v2/pass_ticket_fallback_image_light.png"
        return posterPath
            ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterPath}`
            : defaultImageUrl
    }
    const bgURLDesktop = getDesktopPoster(updatedEvent)
    const bgURLMobile = getMobilePoster(updatedEvent)

    const pageTitle = `${
        isAr ? updatedEvent?.ar_title : updatedEvent?.en_title
    } | ${isAr ? "باس تيكيتس" : "PassTickets"}`

    const description = isAr
        ? updatedEvent?.ar_description
        : updatedEvent?.en_description || pageTitle

    const cleanDescription = removeMarkdown(description)

    const pageDescription = cleanDescription.slice(0, 160)

    const shortDescription = cleanDescription.slice(0, 100)

    const formatDateISO = (dateString: string) => {
        if (!dateString) return ""
        const date = new Date(dateString)
        return date.toISOString()
    }

    const startDate = formatDateISO(
        updatedEvent?.event_date?.starting_date.toString()
    )
    const endDate = formatDateISO(
        updatedEvent?.event_date?.ending_date.toString()
    )

    const isExpiredEvent = () => {
        const currentDate = new Date()
        const eventEndingDate = new Date(
            updatedEvent?.event_date?.ending_date || ""
        )
        if (currentDate < eventEndingDate) {
            return true
        } else if (currentDate > eventEndingDate) {
            return false
        }
        return false
    }

    const isExpired = isExpiredEvent()

    const { trackEvent } = useAnalytics()
    const { sendEventViewed } = useAnalyticsTracking()
    const eventViewedRef = useRef(false)

    // Track event_viewed when event data is loaded (only once)
    useEffect(() => {
        if (updatedEvent?._id && !eventViewedRef.current) {
            eventViewedRef.current = true
            sendEventViewed(updatedEvent._id, updatedEvent.en_title || updatedEvent.ar_title || '')
        }
    }, [updatedEvent?._id, updatedEvent?.en_title, updatedEvent?.ar_title, sendEventViewed])

    if (router.query.shared) {
        trackEvent(
            "view_item",
            {
                action: "view_item",
                category: "event",
                label: `share_event_${currentF1Id}`,
                value: `${router.query.shared}`,
            }
        )
    }

    if (isLoading) {
        return (
            <F1Layout>
                <EventPageSkeleton />
            </F1Layout>
        )
    }
    if (error) {
        // Handle error state
        return (
            <F1Layout>
                <div>Error loading event data</div>
            </F1Layout>
        )
    }

    const baseUrl = (
        process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
    ).replace(/\/$/, "")
    const currentPath = router.asPath.split("#")[0].split("?", 1)[0] || "/"
    const canonicalUrl = `${baseUrl}${currentPath}`
    const imageUrl = getDesktopPoster(updatedEvent)
    const localePrefix =
        currentPath.match(/^\/(en-US|ar-SA)(?=\/|$)/)?.[0] || ""

    const pageMetaTitle = `${
        isAr ? updatedEvent?.ar_title : updatedEvent?.en_title
    } Formula 1 | ${isAr ? "باس تيكيتس" : "PassTickets"} - Official Tickets`

    const keywords = `${updatedEvent?.en_title}, ${
        updatedEvent?.ar_title
    }, F1, Formula 1,
     tickets, events, Saudi Arabia, ${
         isAr ? "باس تيكيتس" : "PassTickets"
     }, فورمولا 1`

    return (
        <>
            <NextSeo
                title={pageMetaTitle}
                description={pageDescription}
                canonical={canonicalUrl}
                languageAlternates={(() => {
                    const pathNoLocale = currentPath.replace(
                        /^\/(en-US|ar-SA)(?=\/|$)/,
                        ""
                    )
                    const defaultHref = `${baseUrl}${pathNoLocale || "/"}`
                    const enHref = `${baseUrl}/en-US${pathNoLocale || "/"}`
                    return [
                        { hrefLang: "x-default", href: defaultHref },
                        { hrefLang: "ar", href: defaultHref },
                        { hrefLang: "en", href: enHref },
                    ]
                })()}
                openGraph={{
                    type: "website",
                    locale: isAr ? "ar_SA" : "en_US",
                    url: canonicalUrl,
                    title: pageTitle,
                    description: pageDescription,
                    site_name: isAr ? "باس تيكيتس" : "PassTickets",
                    images: [
                        {
                            url: imageUrl,
                            width: 1200,
                            height: 630,
                            alt: isAr
                                ? updatedEvent?.ar_title
                                : updatedEvent?.en_title,
                            type: "image/jpeg",
                        },
                    ],
                }}
                twitter={{
                    handle: "@passtickets",
                    site: "@passtickets",
                    cardType: "summary_large_image",
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content: keywords,
                    },
                    {
                        name: "author",
                        content: isAr ? "باس تيكيتس" : "PassTickets",
                    },
                ]}
            />

            {/* Breadcrumb JSON-LD */}
            <BreadcrumbJsonLd
                useAppDir={false}
                itemListElements={[
                    {
                        position: 1,
                        name: isAr ? "الصفحة الرئيسية" : "Home",
                        item: `${baseUrl}${localePrefix || "/"}`,
                    },
                    {
                        position: 2,
                        name: isAr ? "الفعاليات" : "Events",
                        item: `${baseUrl}${localePrefix}/events`,
                    },
                    {
                        position: 3,
                        name: isAr
                            ? updatedEvent?.ar_title
                            : updatedEvent?.en_title,
                        item: canonicalUrl,
                    },
                ]}
            />

            {/* Event JSON-LD Structured Data */}
            <EventJsonLd
                useAppDir={false}
                url={canonicalUrl}
                name={
                    (isAr ? updatedEvent?.ar_title : updatedEvent?.en_title) ||
                    ""
                }
                description={shortDescription}
                images={[imageUrl]}
                startDate={startDate}
                endDate={endDate}
                location={{
                    name: updatedEvent?.organisation?.en_name || "Saudi Arabia",
                    address: {
                        addressLocality:
                            updatedEvent?.address?.city || "Saudi Arabia",
                    },
                }}
                offers={{
                    url: canonicalUrl,
                    priceCurrency: "SAR",
                    availability:
                        new Date() <
                        new Date(updatedEvent?.event_date?.ending_date || "")
                            ? "https://schema.org/InStock"
                            : "https://schema.org/SoldOut",
                }}
                organizer={{
                    name: isAr ? "باس تيكيتس" : "PassTickets",
                    url: baseUrl,
                }}
            />

            <F1Layout>
                    {/*------ Event Hero ------*/}
                    <div
                        className="fixed left-0 top-0 -z-10 aspect-video max-h-screen lg:h-screen w-full overflow-hidden md:block"
                        style={{ height: isDesktop ? "100vh" : "56.25vw" }}
                    >
                        <EventHero
                            event={updatedEvent}
                            backgroundImage={
                                isTabletOrSmaller ? bgURLMobile : bgURLDesktop
                            }
                            mediaType={"image"}
                        />
                    </div>
                    <div
                        className="transition-all duration-300"
                        style={{
                            marginTop: isDesktop
                                ? "calc(100vh - 250px)"
                                : "56.25vw",
                        }}
                    >
                        <GridEventContent
                            event={updatedEvent}
                            openVerificationModal={openVerificationModal}
                            setOpenVerificationModal={setOpenVerificationModal}
                            isExpiredEvent={isExpired}
                            showActionCard={showActionCard}
                            isHeaderFixed={isHeaderFixed}
                        />
                    </div>
                </F1Layout>
        </>
    )
}

export default F1EventPage
